import type { SupplierActionRequest, SupplierActionResponse } from '../types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasPublicApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodSupplierActionResponseSchema,
  zodSupplierRespondPayloadSchema,
} from './validations';

/**
 * Submits a supplier action response for a call-off via magic link.
 * Available actions: confirm, confirm_with_changes, decline, request_cancel.
 *
 * @param callOffId - The call-off ID.
 * @param token - The magic link token for authorization.
 * @param payload - The supplier action request payload.
 * @returns The updated call-off and available actions.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link-(Public)/operation/supplier_respond_calloffs_magic_link__calloff_id__respond_post
 */
export const supplierRespond = async (
  callOffId: UUID,
  token: string,
  payload: SupplierActionRequest,
) => {
  const requestPayload = {
    ...payload,
    token,
  };

  const response = await vestigasPublicApi
    .post(ENDPOINT.CALL_OFF.SUPPLIER_RESPOND(callOffId), {
      json: requestPayload,
      validate: {
        body: zodSupplierRespondPayloadSchema,
      },
    })
    .json<SupplierActionResponse>();

  const validatedResponse = zodSupplierActionResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Supplier Respond API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
